import type { SpecType } from '@local/lib';

import { Button, Checkbox, Icon } from '@salesforce/design-system-react';
import { useId, useState } from 'react';

import { IconCategory } from '../../../shared/types/Enum';
import { AddSpecMenu } from '../../quality-formulas/components/AddSpecMenu';
import { QualitySpecsWorkbench } from '../../quality-formulas/components/QualitySpecsWorkbench';
import { SPEC_TYPES } from '../../quality-formulas/qualityFormulaMetadata';
import {
  useQualityFormulaStore,
  useQualityFormulaStoreApi,
} from '../../quality-formulas/store/QualityFormulaStoreProvider';
import { LineItemSection } from './LineItemSection';

export const QualityAdjustmentSection: React.FC = () => {
  const specCount = useQualityFormulaStore(store => store.visibleSpecTypes.length);
  const store = useQualityFormulaStoreApi();
  // Gates whether the spec picker/workbench below is shown at all — defaults to on for a line
  // that already has specs configured, so existing quality configs keep rendering as before.
  const [hasQualityAdjustments, setHasQualityAdjustments] = useState(specCount > 0);
  const [isWorkbenchOpen, setIsWorkbenchOpen] = useState(specCount > 0);
  const toggleDescriptionId = useId();
  const showWorkbench = isWorkbenchOpen && specCount > 0;
  const addSpec = (specType: SpecType) => {
    store.getState().addSpec(specType);
    store.getState().setPlaygroundOpen(false);
    store.getState().setTemplateManagerOpen(false);
    setIsWorkbenchOpen(true);
  };
  let specPicker: React.ReactNode = null;
  if (hasQualityAdjustments) {
    specPicker = showWorkbench ? (
      <div className="line-item-editor-quality-workbench">
        <QualitySpecsWorkbench lineItem />
      </div>
    ) : (
      <div className="line-item-editor-quality-empty">
        <div
          aria-hidden="true"
          className="line-item-editor-quality-empty-icon slds-avatar slds-avatar_circle slds-avatar_large"
        >
          <Icon
            category={IconCategory.Utility}
            name="formula"
            size="large"
            style={{ fill: 'var(--slds-g-color-accent-1)' }}
          />
        </div>
        <h3 className="line-item-editor-quality-empty-title slds-text-heading_small">
          {specCount ? `${specCount} quality specs configured` : 'No quality specs on this line yet'}
        </h3>
        <p className="line-item-editor-quality-empty-description slds-text-body_regular slds-text-color_weak">
          {specCount
            ? 'Open the spec workspace to edit formulas or test sample values.'
            : 'Add a quality spec to set its formula, tier mode, and price basis before testing.'}
        </p>
        <div className="slds-grid slds-grid_vertical-align-center slds-wrap" style={{ gap: 'var(--slds-g-spacing-2)' }}>
          {specCount ? (
            <Button
              className="slds-button_small slds-m-vertical_small"
              iconCategory={IconCategory.Utility}
              iconName="edit"
              iconPosition="left"
              label="Edit Quality Specs"
              onClick={() => {
                store.getState().setPlaygroundOpen(false);
                store.getState().setTemplateManagerOpen(false);
                setIsWorkbenchOpen(true);
              }}
              variant="brand"
            />
          ) : (
            <AddSpecMenu availableSpecs={SPEC_TYPES} onAdd={addSpec} onRemove={store.getState().removeSpec}>
              <Button
                className="slds-button_small slds-m-vertical_small"
                iconCategory={IconCategory.Utility}
                iconName="add"
                iconPosition="left"
                label="Add Quality Spec"
                variant="brand"
              />
            </AddSpecMenu>
          )}
        </div>
      </div>
    );
  }

  return (
    <LineItemSection
      defaultOpen
      iconName="formula"
      summary={hasQualityAdjustments && !showWorkbench && specCount ? `${specCount} quality specs configured` : ''}
      title="Quality Adjustment"
    >
      <div className="line-item-editor-quality-toggle-row slds-m-bottom_small">
        <div className="line-item-editor-quality-toggle-copy">
          <p className="slds-text-body_regular line-item-editor-quality-toggle-title">
            Does this line have quality adjustments?
          </p>
          <p className="slds-text-body_small slds-text-color_weak" id={toggleDescriptionId}>
            Turn on to add quality specs and build the adjustment formula for this line only.
          </p>
        </div>
        <Checkbox
          aria-describedby={toggleDescriptionId}
          assistiveText={{ label: 'Does this line have quality adjustments?' }}
          checked={hasQualityAdjustments}
          className="line-item-editor-quality-toggle-control"
          labels={{ toggleDisabled: 'No', toggleEnabled: 'Yes' }}
          onChange={(_event, data) => {
            setHasQualityAdjustments(data.checked);
          }}
          variant="toggle"
        />
      </div>
      {specPicker}
    </LineItemSection>
  );
};
